import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../src/functions/useLanguage';
import { wt } from './i18n';
import { Navbar } from './components/Navbar';
import { PetSittersNavbar } from './components/PetSittersNavbar';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { Blog } from './pages/Blog';
import { FAQ } from './pages/FAQ';
import { TheVitalCheck } from './pages/posts/TheVitalCheck';
import { TheCareCircle } from './pages/posts/TheCareCircle';
import { TheLabStory } from './pages/posts/TheLabStory';
import { TheHabitLoop } from './pages/posts/TheHabitLoop';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { PetSitters } from './pages/PetSitters';
import { CookieConsent } from './components/CookieConsent';

export default function WebsiteApp() {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);

  const normalisedPath = location.pathname.replace(/^\/website/, '') || '/';
  const isPetSittersRoute = normalisedPath === '/petsitters';
  const isWebsiteHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'kodacare.pet' ||
      window.location.hostname === 'www.kodacare.pet' ||
      window.location.hostname.includes('kodacare-website'));

  // Backward compatibility: old internal links may still point to /website/*.
  // On the public website host, canonical routes are now root-based (/, /blog, /faq, ...).
  useEffect(() => {
    if (!isWebsiteHost || !location.pathname.startsWith('/website')) return;
    const nextPath = location.pathname.replace(/^\/website/, '') || '/';
    navigate(`${nextPath}${location.search}${location.hash}`, { replace: true });
  }, [isWebsiteHost, location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    const isSpecialRoute =
      normalisedPath.startsWith('/blog/') ||
      normalisedPath === '/privacy' ||
      normalisedPath === '/terms' ||
      normalisedPath === '/faq' ||
      normalisedPath === '/petsitters';

    if (isSpecialRoute) {
      return;
    }

    const localizedTitle = `KodaCare — ${t('site.titleSuffix')}`;
    const localizedDescription = t('site.description');
    const canonicalUrl = `https://kodacare.pet${normalisedPath === '/' ? '' : normalisedPath}`;
    document.title = localizedTitle;

    const setMeta = (selector: string, content: string) => {
      const el = document.querySelector(selector) as HTMLMetaElement | null;
      if (el) el.content = content;
    };

    setMeta('meta[name="description"]', localizedDescription);
    setMeta('meta[property="og:title"]', localizedTitle);
    setMeta('meta[property="og:description"]', localizedDescription);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[name="twitter:title"]', localizedTitle);
    setMeta('meta[name="twitter:description"]', localizedDescription);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
  }, [language, normalisedPath, t]);

  // Scroll to hash anchor after navigation (e.g. /website#pricing)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Retry a few times in case the target section hasn't rendered yet
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempts < 10) {
        attempts++;
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, [location.hash, location.pathname]);

  return (
    <div className="koda-website min-h-screen flex flex-col font-sans">
      {isPetSittersRoute ? <PetSittersNavbar /> : <Navbar />}
      <div className="flex-1">
        <Routes>
          {/* Paths are relative to the matched parent route (/website) */}
          <Route index element={<Home />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/the-vital-check" element={<TheVitalCheck />} />
          <Route path="blog/the-care-circle" element={<TheCareCircle />} />
          <Route path="blog/the-lab-story" element={<TheLabStory />} />
          <Route path="blog/the-habit-loop" element={<TheHabitLoop />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="petsitters" element={<PetSitters />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfService />} />
        </Routes>
      </div>
      <Footer />
      <CookieConsent />
    </div>
  );
}
